/* MedLora: accessible, AJAX-based quick add and shopping bag drawer.
   The underlying product forms and full cart continue working without JavaScript. */
(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  ready(() => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || typeof drawer.showModal !== 'function' || !window.fetch) return;

    const root = (window.Shopify?.routes?.root || drawer.dataset.cartRoot || '/').replace(/\/?$/, '/');
    const api = (endpoint) => `${root}cart/${endpoint}.js`;
    const body = drawer.querySelector('[data-cart-body]');
    const footer = drawer.querySelector('[data-cart-footer]');
    const status = drawer.querySelector('[data-cart-status]');
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    const savingsRow = drawer.querySelector('[data-cart-savings-row]');
    const savingsValue = drawer.querySelector('[data-cart-savings]');
    const savingsNote = drawer.querySelector('[data-cart-savings-note]');
    const productCache = new Map();
    let savingsVersion = 0;
    let lastOpener = null;
    let updating = false;
    let refreshVersion = 0;

    function node(tag, className, content) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (content !== undefined) element.textContent = String(content);
      return element;
    }

    function money(cents, currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currency || drawer.dataset.cartCurrency || 'USD'
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return (Number(cents || 0) / 100).toFixed(2);
      }
    }

    async function request(url, options) {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, ...options });
      if (!response.ok) {
        let error;
        try { error = await response.json(); } catch (_) { /* Not a JSON response */ }
        throw new Error(typeof error?.description === 'string' ? error.description :
          (typeof error?.message === 'string' ? error.message : 'Could not update your bag. Please try again.'));
      }
      return response.json();
    }

    // The cart Ajax payload omits the variant's compare-at price. Fetch each
    // product once and use its *selected variant*; never assume a product's
    // maximum compare-at price applies to all variants. Shopify's localized
    // product Ajax endpoint and cart Ajax endpoint use presentment currency.
    function compareAtForItem(item, product) {
      const original = Number(item.original_price) || 0;
      const variant = product?.variants?.find((value) => String(value.id) === String(item.variant_id));
      const compareAt = Number(variant?.compare_at_price) || 0;
      return Math.max(original, compareAt);
    }

    function productForItem(item) {
      if (!item.handle || !item.variant_id) return Promise.resolve(null);
      if (!productCache.has(item.handle)) {
        productCache.set(item.handle,
          request(`${root}products/${encodeURIComponent(item.handle)}.js`, { method: 'GET' })
            .catch(() => null));
      }
      return productCache.get(item.handle);
    }

    async function updateSavings(cart, items, version) {
      if (!savingsRow || !savingsValue || !savingsNote) return;
      savingsRow.hidden = true;
      savingsNote.hidden = true;
      if (!items.length) return;
      const products = await Promise.all(items.map(productForItem));
      if (version !== savingsVersion || footer.hidden) return;
      let referenceTotal = 0;
      items.forEach((item, index) => {
        const regularUnit = compareAtForItem(item, products[index]);
        const quantity = Number(item.quantity) || 0;
        referenceTotal += regularUnit * quantity;
        // Add the strikethrough unit price only when the source supports it.
        const unitPrice = body.querySelectorAll('.ml-cart__unit-price')[index];
        if (unitPrice && regularUnit > Number(item.final_price)) {
          const oldPrice = node('s', 'ml-cart__regular-price', money(regularUnit, cart.currency));
          unitPrice.append(document.createTextNode('  '), oldPrice);
        }
      });
      // Includes compare-at savings plus any automatic / cart discounts once.
      const amountSaved = Math.max(0, referenceTotal - Number(cart.total_price || 0));
      if (amountSaved > 0) {
        savingsValue.textContent = money(amountSaved, cart.currency);
        savingsRow.hidden = false;
        savingsNote.hidden = false;
      }
    }

    function message(text, isError = false) {
      status.hidden = !text;
      status.textContent = text || '';
      status.classList.toggle('ml-cart__status--error', isError);
    }

    function updateCounts(cart) {
      const count = Number(cart.item_count) || 0;
      document.querySelectorAll('.cart-shortcut').forEach((button) => {
        button.setAttribute('aria-label', `Shopping bag, ${count} ${count === 1 ? 'item' : 'items'}`);
        const badge = button.querySelector('.cart-count');
        if (badge) badge.textContent = String(count);
      });
      const drawerCount = drawer.querySelector('[data-cart-drawer-count]');
      if (drawerCount) drawerCount.textContent = `(${count})`;
      document.dispatchEvent(new CustomEvent('medlora:cart-updated', { detail: { cart } }));
    }

    function createItem(item, currency) {
      const row = node('article', 'ml-cart__item');
      const imageLink = node('a', 'ml-cart__image');
      imageLink.href = item.url || root;
      if (item.image) {
        const image = node('img');
        image.src = item.image;
        image.alt = item.product_title || item.title || '';
        image.loading = 'lazy';
        imageLink.append(image);
      } else imageLink.append(node('span', '', '✳'));
      row.append(imageLink);

      const details = node('div', 'ml-cart__details');
      const title = node('a', 'ml-cart__item-title', item.product_title || item.title || 'Product');
      title.href = item.url || root;
      details.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        details.append(node('p', 'ml-cart__variant', item.variant_title));
      }
      details.append(node('p', 'ml-cart__unit-price', `${money(item.final_price, currency)} each`));

      const actions = node('div', 'ml-cart__item-actions');
      const quantity = node('div', 'ml-cart__quantity');
      quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title || 'product'}`);
      const minus = node('button', '', '−');
      minus.type = 'button';
      minus.dataset.cartAction = 'decrease';
      minus.dataset.cartLine = item.key;
      minus.dataset.cartQuantity = item.quantity;
      minus.setAttribute('aria-label', `Decrease quantity of ${item.product_title || item.title}`);
      const current = node('span', '', item.quantity);
      current.setAttribute('aria-live', 'polite');
      const plus = node('button', '', '+');
      plus.type = 'button';
      plus.dataset.cartAction = 'increase';
      plus.dataset.cartLine = item.key;
      plus.dataset.cartQuantity = item.quantity;
      plus.setAttribute('aria-label', `Increase quantity of ${item.product_title || item.title}`);
      plus.disabled = item.quantity >= 99;
      quantity.append(minus, current, plus);
      const remove = node('button', 'ml-cart__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.cartAction = 'remove';
      remove.dataset.cartLine = item.key;
      remove.setAttribute('aria-label', `Remove ${item.product_title || item.title} from bag`);
      actions.append(quantity, remove);
      details.append(actions);
      row.append(details, node('strong', 'ml-cart__item-total', money(item.final_line_price, currency)));
      return row;
    }

    function render(cart, { resetScroll = false } = {}) {
      updateCounts(cart);
      const savingsRun = ++savingsVersion;
      const oldScroll = body.scrollTop;
      const currency = cart.currency || drawer.dataset.cartCurrency;
      const items = Array.isArray(cart.items) ? cart.items : [];
      body.replaceChildren();
      if (!items.length) {
        const empty = node('div', 'ml-cart__empty');
        empty.append(node('span', 'ml-cart__empty-icon', '✳'));
        empty.append(node('h3', '', 'Your bag is empty.'));
        empty.append(node('p', '', 'Explore the library and add something worth learning.'));
        const continueButton = node('button', 'ml-cart__empty-continue', 'Continue shopping ↗');
        continueButton.type = 'button';
        continueButton.dataset.cartClose = '';
        empty.append(continueButton);
        body.append(empty);
        footer.hidden = true;
      } else {
        items.forEach((item) => body.append(createItem(item, currency)));
        footer.hidden = false;
        subtotal.textContent = money(cart.total_price, currency);
      }
      updateSavings(cart, items, savingsRun).catch(() => {
        if (savingsRun === savingsVersion && savingsRow) savingsRow.hidden = true;
      });
      body.scrollTop = resetScroll ? 0 : oldScroll;
      body.setAttribute('aria-busy', 'false');
    }

    function renderLoadError() {
      ++savingsVersion;
      body.setAttribute('aria-busy', 'false');
      const error = node('div', 'ml-cart__load-error');
      error.append(node('p', '', 'Your bag could not be loaded. You can try again or use the full cart.'));
      const retry = node('button', 'ml-cart__retry', 'Try again');
      retry.type = 'button';
      retry.dataset.cartRetry = '';
      error.append(retry);
      body.replaceChildren(error);
      footer.hidden = true;
    }

    async function fetchCart({ resetScroll = false } = {}) {
      const version = ++refreshVersion;
      const cart = await request(`${root}cart.js`, { method: 'GET' });
      if (version === refreshVersion) render(cart, { resetScroll });
      return cart;
    }

    async function openDrawer({ added = false, opener = document.activeElement } = {}) {
      if (!drawer.open) {
        lastOpener = opener;
        drawer.showModal();
        drawer.querySelector('[data-cart-close]')?.focus();
      }
      message(added ? 'Added to your bag.' : '');
      body.setAttribute('aria-busy', 'true');
      if (!body.querySelector('.ml-cart__item')) body.replaceChildren(node('p', 'ml-cart__loading', 'Loading your bag…'));
      try {
        await fetchCart({ resetScroll: added });
      } catch (_) {
        renderLoadError();
        message(added ? 'Added to your bag, but the bag could not be loaded.' : 'Unable to load your bag.', true);
      }
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-toggle]');
      if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openDrawer({ opener: trigger });
    });

    drawer.addEventListener('click', (event) => {
      const close = event.target.closest('[data-cart-close]');
      if (close) { drawer.close(); return; }
      if (event.target.closest('[data-cart-retry]')) { openDrawer(); return; }

      const button = event.target.closest('[data-cart-action]');
      if (!button || updating) return;
      const action = button.dataset.cartAction;
      const key = button.dataset.cartLine;
      const quantity = Number(button.dataset.cartQuantity) || 0;
      const next = action === 'remove' ? 0 : action === 'increase' ? quantity + 1 : Math.max(0, quantity - 1);
      if (!key || next > 99) return;
      updateLine(key, next, action);
    });

    drawer.addEventListener('click', (event) => {
      if (event.target !== drawer) return;
      const rect = drawer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) drawer.close();
    });

    drawer.addEventListener('close', () => {
      message('');
      if (lastOpener?.isConnected) lastOpener.focus({ preventScroll: true });
      lastOpener = null;
    });

    async function updateLine(key, quantity, action) {
      updating = true;
      message('Updating your bag…');
      body.setAttribute('aria-busy', 'true');
      body.querySelectorAll('[data-cart-action]').forEach((button) => { button.disabled = true; });
      try {
        const cart = await request(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        render(cart);
        message('Bag updated.');
        const newButton = [...body.querySelectorAll('[data-cart-action]')]
          .find((button) => button.dataset.cartLine === key && button.dataset.cartAction === action);
        (newButton || body.querySelector('[data-cart-action]') || drawer.querySelector('[data-cart-close]'))?.focus({ preventScroll: true });
      } catch (error) {
        message(error.message || 'Unable to update your bag.', true);
      } finally {
        updating = false;
        body.setAttribute('aria-busy', 'false');
        body.querySelectorAll('[data-cart-action]').forEach((button) => {
          button.disabled = button.dataset.cartAction === 'increase' && Number(button.dataset.cartQuantity) >= 99;
        });
      }
    }

    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-quick-variant]');
      if (!select) return;
      const form = select.closest('form');
      const option = select.selectedOptions[0];
      const button = form?.querySelector('[data-cart-add-button]');
      const label = button?.querySelector('[data-add-label]');
      if (button) button.disabled = option?.dataset.available !== 'true';
      if (label) label.textContent = button.disabled ? 'Sold out' : 'Add to bag';
      const price = form?.closest('.product-card, .ml-spotlight__item')?.querySelector('.product-card__price, .ml-spotlight__price');
      if (price && option) {
        price.replaceChildren(document.createTextNode(option.dataset.price || ''));
        if (option.dataset.compare) price.append(node('s', '', option.dataset.compare));
      }
      const error = form?.querySelector('[data-cart-add-error]');
      if (error) error.hidden = true;
    });

    // An open edition picker should not obscure nearby product cards.
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-details]')) return;
      document.querySelectorAll('[data-quick-details][open]').forEach((details) => { details.open = false; });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-quick-details][open]').forEach((details) => { details.open = false; });
    });
    document.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details.matches?.('[data-quick-details]') || !details.open) return;
      document.querySelectorAll('[data-quick-details][open]').forEach((other) => { if (other !== details) other.open = false; });
    }, true);

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('[data-cart-add], .ml-product__form, .mps__form, .ml-spotlight__quick-form')) return;
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (form.dataset.cartSubmitting === 'true') return;
      form.dataset.cartSubmitting = 'true';
      const button = form.querySelector('[name="add"]');
      const label = button?.querySelector('[data-add-label], span');
      const originalLabel = label?.textContent;
      const originalDisabled = button?.disabled;
      const error = form.querySelector('[data-cart-add-error]') || node('p', 'ml-cart__form-error');
      if (!error.parentNode) {
        error.setAttribute('role', 'alert');
        form.append(error);
      }
      error.hidden = true;
      if (button) button.disabled = true;
      if (label) label.textContent = 'Adding…';
      try {
        await request(`${root}cart/add.js`, { method: 'POST', body: new FormData(form) });
        await openDrawer({ added: true, opener: button || form });
      } catch (problem) {
        error.textContent = problem.message || 'Could not add this item. Please try again.';
        error.hidden = false;
      } finally {
        form.dataset.cartSubmitting = 'false';
        if (button) button.disabled = Boolean(originalDisabled);
        if (label) label.textContent = originalLabel;
      }
    });

    // Refresh the header count after navigating back from checkout/cart in the browser.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) fetchCart().catch(() => {});
    });
  });
})();
